import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
export const useAuthStore = defineStore('auth', () => {
    const admin = ref(null);
    const token = ref(null);
    const loading = ref(false);
    const toast = useToast();
    const isAuthenticated = computed(() => !!token.value && !!admin.value);
    const initializeAuth = () => {
        const storedToken = localStorage.getItem('auth_token');
        const storedAdmin = localStorage.getItem('auth_admin');
        if (storedToken && storedAdmin) {
            token.value = storedToken;
            admin.value = JSON.parse(storedAdmin);
            // Set axios default header
            axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        }
    };
    const login = async (credentials) => {
        loading.value = true;
        try {
            const response = await axios.post('/api/auth/login', credentials);
            if (response.data.success) {
                const { admin: adminData, token: tokenData } = response.data.data;
                admin.value = adminData;
                token.value = tokenData;
                // Store in localStorage
                localStorage.setItem('auth_token', tokenData);
                localStorage.setItem('auth_admin', JSON.stringify(adminData));
                // Set axios default header
                axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
                toast.success('Login successful!');
                return true;
            }
        }
        catch (error) {
            const message = error.response?.data?.error || 'Login failed';
            toast.error(message);
            return false;
        }
        finally {
            loading.value = false;
        }
    };
    const register = async (data) => {
        loading.value = true;
        try {
            const response = await axios.post('/api/auth/register', data);
            if (response.data.success) {
                const { admin: adminData, token: tokenData } = response.data.data;
                admin.value = adminData;
                token.value = tokenData;
                // Store in localStorage
                localStorage.setItem('auth_token', tokenData);
                localStorage.setItem('auth_admin', JSON.stringify(adminData));
                // Set axios default header
                axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
                toast.success('Registration successful!');
                return true;
            }
        }
        catch (error) {
            const message = error.response?.data?.error || 'Registration failed';
            toast.error(message);
            return false;
        }
        finally {
            loading.value = false;
        }
    };
    const logout = () => {
        admin.value = null;
        token.value = null;
        // Remove from localStorage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_admin');
        // Remove axios default header
        delete axios.defaults.headers.common['Authorization'];
        toast.success('Logged out successfully');
    };
    const fetchCurrentAdmin = async () => {
        try {
            const response = await axios.get('/api/auth/me');
            if (response.data.success) {
                admin.value = response.data.data;
                localStorage.setItem('auth_admin', JSON.stringify(response.data.data));
            }
        }
        catch (error) {
            // If token is invalid, logout
            logout();
        }
    };
    // Role-based computed properties
    const isSuperAdmin = computed(() => admin.value?.role === 'SUPER_ADMIN');
    const isAdmin = computed(() => admin.value?.role === 'ADMIN' || admin.value?.role === 'SUPER_ADMIN');
    return {
        admin: computed(() => admin.value),
        token: computed(() => token.value),
        loading: computed(() => loading.value),
        isAuthenticated,
        isSuperAdmin,
        isAdmin,
        initializeAuth,
        login,
        register,
        logout,
        fetchCurrentAdmin
    };
});
